import { AfterViewInit, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { scheduleTypes } from '../../constants/constants';
import { AbstractControl, FormGroup, Validators } from '@angular/forms';
import { parseSearchTokens } from '../../utils/string.utils';
import { pairwise, startWith } from 'rxjs';
import { ScheduleVM } from '../schedule-select/schedule-select.component';
import BaseComponent from '../base.component';
import * as tooltips from '../../constants/tooltips';
import { TenantScheduleSummary } from '../../models/tenant-schedule-summary';
import { CronService } from '../cron-editor/cron.service';

@Component({
    selector: 'cdp-schedule-jobs',
    templateUrl: './schedule-jobs.component.html',
    styleUrls: ['./schedule-jobs.component.scss'],
    standalone: false
})
export class ScheduleJobsComponent extends BaseComponent implements OnInit, AfterViewInit {
    @Input() form: FormGroup;
    @Input() tenantId!: string;
    @Input() scheduleIsRequired: boolean;
    @Input() schedules: TenantScheduleSummary[];
    @Input() hasSourcesEditPermission = false;
    @Input() showSelectedSchedLink = false;

    scheduleTypes = scheduleTypes;
    existingSchedHelp = tooltips.existingSchedHelp;
    newSchedHelp = tooltips.newSchedHelp;
    currentCronStr = '';
    isLoading = false;

    constructor(
        private cd: ChangeDetectorRef,
        private cronService: CronService
    ) {
        super();
    }
    ngAfterViewInit(): void {
        this.cd.detectChanges();
    }

    ngOnInit(): void {
        this.init();
    }

    isExistingSchedule(): boolean {
        return this.form.value.scheduleType === scheduleTypes.Existing;
    }

    isNewSchedule(): boolean {
        return this.form.value.scheduleType === scheduleTypes.New;
    }

    onChangeSchedule(selection: ScheduleVM) {
        this.form.controls['scheduleCron'].setValue(selection?.rawCron);
        this.form.controls['scheduleDescription'].setValue(selection?.name);
        this.currentCronStr = this.cronService.translateCronWithUtc(selection?.rawCron);
    }

    onCronChange(data: { rawCron: string; formGroup: FormGroup; controlName: string }) {
        // update the sched name if the user hasn't explicitly set it to something other than the cron string
        const newCronStr = this.cronService.translateCronWithUtc(data.rawCron);
        const currentSchedName = this.form.controls['scheduleDescription'].value;
        if (!currentSchedName || this.currentCronStr === currentSchedName) {
            this.form.controls['scheduleDescription'].setValue(newCronStr);
        }
        this.currentCronStr = newCronStr;

        data.formGroup.controls[data.controlName].setValue(data.rawCron);
        data.formGroup.controls[data.controlName].markAsDirty();
    }

    setNameToMatchCron(): void {
        this.form.controls['scheduleDescription'].setValue(this.currentCronStr);
    }

    get scheduleControl(): AbstractControl<any, any> {
        return this.form.controls['scheduleId'];
    }

    schedSearchFn(term: string, item: ScheduleVM): boolean {
        const tokens = parseSearchTokens(term);
        const searchIn = `${item.name} ${item.scheduleDesc}`.toLocaleLowerCase();
        return tokens.every(token => searchIn.indexOf(token) > -1);
    }

    clearSchedule() {
        this.form.get('scheduleId')?.setValue(null);
        this.form.get('scheduleType')?.setValue(this.scheduleTypes.Existing);
        this.form.get('scheduleCron')?.setValue('');
        this.form.get('scheduleDescription')?.setValue('');
    }

    onScheduleRadioClick() {
        const existingSched = this.schedules.find(s => s.tenantScheduleId === this.form.value.scheduleId);
        if (this.isExistingSchedule()) {
            this.form.get('scheduleCron')?.setValue(existingSched?.cron);
        }
    }

    private init() {
        this.form.valueChanges
            .pipe(startWith(undefined), pairwise(), this.unsubscribeOnDestroy())
            .subscribe(([prev, next]) => {
                if (next.scheduleType !== prev?.scheduleType) {
                    if (next.scheduleType == scheduleTypes.New) {
                        this.form.get('scheduleDescription')?.setValidators([Validators.required]);
                    } else {
                        this.form.get('scheduleDescription')?.clearValidators();
                    }

                    if (this.scheduleIsRequired) {
                        if (next.scheduleType == scheduleTypes.Existing) {
                            this.form.get('scheduleId')?.setValidators([Validators.required]);
                        } else {
                            this.form.get('scheduleId')?.clearValidators();
                        }
                        this.form.get('scheduleId')?.updateValueAndValidity();
                    }

                    this.form.get('scheduleDescription')?.updateValueAndValidity();
                }
            });
    }
}
